import { getOnboardingModuleSteps } from '@/components/onboarding/steps/onboardingModuleSteps';
import { ONBOARDING_TARGETS } from '@/constants/onboarding';
import { useAppDispatch, useAppSelector } from '@/state/hooks';
import {
    clearHighlightedTarget,
    selectHelperHighlightedTarget,
    setHighlightedTarget
} from '@/state/reducers/helperSlice';
import useLanguage from '@/utils/hooks/useLanguage';
import useTourManager from '@/utils/hooks/useTourManager';
import { useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Modal, { ModalClose, ModalHeader, ModalTitle } from './Modal';

type HelperAction = 'highlight' | 'tour';

interface HelperFeature {
    id: string;
    title: string;
    description: string;
    keywords: string[];
    action: HelperAction;
    targetSelector?: string;
}

interface Message {
    role: 'assistant' | 'user';
    text: string;
    matches?: HelperFeature[];
}

interface HelperModalProps {
    opened: boolean;
    onClose: () => void;
}

const HelperModal = ({ opened, onClose }: HelperModalProps) => {
    const dispatch = useAppDispatch();
    const lang = useLanguage();
    const { replayTour } = useTourManager();
    const { pathname } = useLocation();
    const highlightedTarget = useAppSelector(selectHelperHighlightedTarget);

    const [prompt, setPrompt] = useState('');
    const [message, setMessage] = useState<Message>({
        role: 'assistant',
        text: lang.helper.subtitle
    });

    const isTraderRoute = pathname.includes('/trader');

    const helperFeatures = useMemo<HelperFeature[]>(() => {
        const baseFeatures: HelperFeature[] = [
            {
                id: 'workspace-controls',
                title: lang.workspace.workspace,
                description: lang.helper.workspaceDescription,
                keywords: lang.helper.workspaceKeywords,
                action: 'highlight',
                targetSelector: ONBOARDING_TARGETS.workspaceControls
            },
            {
                id: 'add-module',
                title: lang.workspace.addModule,
                description: lang.helper.addModuleDescription,
                keywords: lang.helper.addModuleKeywords,
                action: 'highlight',
                targetSelector: ONBOARDING_TARGETS.addModule
            },
            {
                id: 'account-navigation',
                title: lang.navigation.tradingPlatforms,
                description: `${lang.navigation.deposits}, ${lang.navigation.withdrawals}, ${lang.navigation['bank accounts']}, ${lang.navigation.wallets}`,
                keywords: lang.helper.accountNavigationKeywords,
                action: 'highlight',
                targetSelector: ONBOARDING_TARGETS.accountNavigation
            },
            {
                id: 'notifications',
                title: lang.settings.notificationsTab,
                description: lang.helper.notificationsDescription,
                keywords: lang.helper.notificationsKeywords,
                action: 'highlight',
                targetSelector: ONBOARDING_TARGETS.notifications
            },
            {
                id: 'start-tour',
                title: lang.helper.startTour,
                description: lang.helper.tourDescription,
                keywords: lang.helper.startTourKeywords,
                action: 'tour'
            }
        ];

        if (!isTraderRoute) {
            return baseFeatures;
        }

        const traderModuleFeatures: HelperFeature[] = getOnboardingModuleSteps(isTraderRoute).map((step) => ({
            id: step.id,
            title: lang.onboarding[step.copyKey]?.title || '',
            description: lang.helper[step.helperDescriptionKey],
            keywords: lang.helper[step.helperKeywordsKey],
            action: 'highlight',
            targetSelector: step.targetSelector
        }));

        return [...baseFeatures, ...traderModuleFeatures];
    }, [isTraderRoute, lang]);

    const suggestionPrompts = useMemo(
        () => [
            lang.helper.exampleWorkspace,
            lang.helper.exampleOrderBook,
            lang.helper.exampleSettings,
            lang.helper.exampleTour
        ],
        [lang]
    );

    const onSubmitPrompt = useCallback(
        (submittedPrompt: string) => {
            const normalizedPrompt = submittedPrompt.trim().toLowerCase();
            if (!normalizedPrompt) {
                return;
            }

            const matches = helperFeatures.filter((feature) =>
                feature.keywords.some((keyword) => normalizedPrompt.includes(keyword))
            );

            const assistantMessage =
                matches.length > 0
                    ? lang.helper.foundFeatures
                    : `${lang.helper.noMatchesTitle} ${lang.helper.noMatchesBody}`;

            setMessage({
                role: 'user',
                text: submittedPrompt.trim()
            });
            setMessage({
                role: 'assistant',
                text: assistantMessage,
                matches
            });
            setPrompt('');
        },
        [helperFeatures, lang]
    );

    const onHighlightFeature = useCallback(
        (feature: HelperFeature) => {
            if (!feature.targetSelector) {
                return;
            }

            const targetElement = document.querySelector(feature.targetSelector);
            if (!targetElement) {
                setMessage({
                    role: 'assistant',
                    text: `${lang.helper.featureNotVisible} ${feature.title}.`
                });
                return;
            }

            dispatch(
                setHighlightedTarget({
                    selector: feature.targetSelector,
                    featureLabel: feature.title
                })
            );
            onClose();
        },
        [dispatch, lang, onClose]
    );

    const onStartTour = useCallback(() => {
        dispatch(clearHighlightedTarget());
        replayTour();
        onClose();
    }, [dispatch, onClose, replayTour]);

    const closeHelper = useCallback(() => {
        dispatch(clearHighlightedTarget());
        onClose();
    }, [dispatch, onClose]);

    const handlers = useMemo(
        () => ({
            open: () => {},
            close: closeHelper,
            toggle: closeHelper
        }),
        [closeHelper]
    );

    return (
        <Modal opened={opened} handlers={handlers} size="max-w-2xl">
            <div className="flex flex-col h-full max-h-[80vh]">
                <ModalHeader>
                    <div className="flex items-center justify-between w-full">
                        <ModalTitle>{lang.helper.title}</ModalTitle>
                        <ModalClose handlers={handlers} />
                    </div>
                </ModalHeader>

                <div className="flex-1 overflow-y-auto p-4 pt-0 space-y-3">
                    {message && (
                        <div
                            className={`rounded-md ${
                                message.role === 'assistant'
                                    ? 'bg-brand-neutral-800 text-brand-text'
                                    : 'bg-brand-primary text-brand-module-title'
                            }`}>
                            <p className="text-sm">{message.text}</p>
                            {message.role === 'assistant' && message.matches && message.matches.length > 0 && (
                                <div className="mt-3 space-y-2">
                                    {message.matches.map((feature) => (
                                        <div
                                            key={feature.id}
                                            className="rounded-md border border-brand-neutral-600 p-3 bg-brand-background">
                                            <p className="font-semibold text-sm text-brand-text">{feature.title}</p>
                                            <p className="text-xs text-brand-neutral-400 mt-1">{feature.description}</p>
                                            <div className="mt-2">
                                                {feature.action === 'tour' ? (
                                                    <button
                                                        type="button"
                                                        className="rounded-md p-2 px-3 bg-brand-primary hover:bg-brand-primary-light text-xs text-brand-module-title"
                                                        onClick={onStartTour}>
                                                        {lang.helper.startTour}
                                                    </button>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        className="rounded-md p-2 px-3 bg-brand-neutral-700 hover:bg-brand-neutral-600 text-xs text-brand-text"
                                                        onClick={() => onHighlightFeature(feature)}>
                                                        {lang.helper.highlightFeature}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="border-t border-brand-neutral-700 p-4 space-y-3">
                    <div>
                        <p className="text-xs text-brand-neutral-400 mb-2">{lang.helper.suggestionsTitle}</p>
                        <div className="flex flex-wrap gap-2">
                            {suggestionPrompts.map((suggestion) => (
                                <button
                                    key={suggestion}
                                    type="button"
                                    className="rounded-md p-2 px-3 text-xs bg-brand-neutral-700 hover:bg-brand-neutral-600 text-brand-text"
                                    onClick={() => onSubmitPrompt(suggestion)}>
                                    {suggestion}
                                </button>
                            ))}
                        </div>
                    </div>

                    <form
                        className="flex gap-2"
                        onSubmit={(event) => {
                            event.preventDefault();
                            onSubmitPrompt(prompt);
                        }}>
                        <input
                            type="text"
                            className="flex-1 rounded-md bg-brand-background-dark border border-brand-neutral-700 px-3 py-2 text-sm text-brand-text focus:outline-none focus:border-brand-primary"
                            value={prompt}
                            onChange={(event) => setPrompt(event.target.value)}
                            placeholder={lang.helper.inputPlaceholder}
                        />
                        <button
                            type="submit"
                            className="rounded-md px-4 py-2 bg-brand-primary hover:bg-brand-primary-light text-sm text-brand-module-title">
                            {lang.helper.askButton}
                        </button>
                    </form>
                </div>
            </div>
        </Modal>
    );
};

export default HelperModal;
